import type { ActivityDefinition } from '@medplum/fhirtypes';

import {
  ATLAS_EXERCISE_LIBRARY_TAG_CODE,
  ATLAS_RESOURCE_TAG_SYSTEM,
  CARE_LIBRARY_TAG_CODE,
  CATALOG_MEMBERSHIP_SYSTEM,
  CATEGORY_AXIS_SYSTEM,
  OFFERING_TYPE_AXIS_CODE,
} from './constants';
import {
  fromDosageExtension,
  fromFhirPathStringLiteral,
  fromTiming,
} from './shared';
import type { ActivityDosage, Cadence } from './spec';

export const isCareLibraryMember = (ad: ActivityDefinition): boolean =>
  (ad.meta?.tag ?? []).some(
    (t) =>
      (t.system === CATALOG_MEMBERSHIP_SYSTEM &&
        t.code === CARE_LIBRARY_TAG_CODE) ||
      (t.system === ATLAS_RESOURCE_TAG_SYSTEM &&
        (t.code === CARE_LIBRARY_TAG_CODE ||
          t.code === ATLAS_EXERCISE_LIBRARY_TAG_CODE)),
  );
export const careLibraryType = (ad: ActivityDefinition): string | undefined => {
  for (const uc of ad.useContext ?? []) {
    if (
      uc.code?.system === CATEGORY_AXIS_SYSTEM &&
      uc.code.code === OFFERING_TYPE_AXIS_CODE
    ) {
      return uc.valueCodeableConcept?.coding?.[0]?.code;
    }
  }
  return undefined;
};
export const isExerciseOffering = (ad: ActivityDefinition): boolean => {
  const type = careLibraryType(ad);
  if (type !== undefined) {
    return type === 'exercise';
  }
  return exerciseDosageOf(ad) !== undefined;
};
export const exerciseDosageOf = (
  ad: ActivityDefinition,
): ActivityDosage | undefined => fromDosageExtension(ad.extension);
export const defaultCadence = (ad: ActivityDefinition): Cadence | undefined =>
  fromTiming(ad.timingTiming).cadence;
export const patientInstructionsOf = (
  ad: ActivityDefinition,
): string | undefined =>
  fromFhirPathStringLiteral(
    ad.dynamicValue?.find((d) => d.path === 'patientInstruction')?.expression
      ?.expression,
  );
export const formatDosageSummary = (
  dosage: ActivityDosage | undefined,
  cadence: Cadence | undefined,
): string | undefined => {
  const parts: string[] = [];
  if (dosage) {
    if (dosage.sets && dosage.reps) {
      parts.push(`${dosage.sets}×${dosage.reps}`);
    } else if (dosage.sets) {
      parts.push(`${dosage.sets} sets`);
    } else if (dosage.reps) {
      parts.push(`${dosage.reps} reps`);
    }
    if (dosage.holdSeconds) {
      parts.push(`hold ${dosage.holdSeconds}s`);
    }
    if (dosage.resistance) {
      parts.push(dosage.resistance);
    }
  }
  if (cadence) {
    const unit = { d: 'day', wk: 'week', mo: 'month' }[cadence.periodUnit];
    const per =
      cadence.period && cadence.period !== 1
        ? `${cadence.period} ${unit}s`
        : unit;
    parts.push(`${cadence.frequency}×/${per}`);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
};
export const exerciseDosageSummary = (
  ad: ActivityDefinition,
): string | undefined =>
  formatDosageSummary(exerciseDosageOf(ad), defaultCadence(ad));
export const careLibraryMetaLine = (
  ad: ActivityDefinition,
): string | undefined => ad.usage ?? exerciseDosageSummary(ad);
